import { Check, Copy, Link, Plus, Users } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { ReferralStatus } from "@/lib/referral";
import { lookupReferralStatus } from "@/lib/referral";
import { isEmail } from "@/lib/waitlist";

export function Referral() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stateLookup =
    location.state &&
    typeof location.state === "object" &&
    "lookup" in location.state &&
    typeof location.state.lookup === "string"
      ? location.state.lookup.trim()
      : "";
  const queryLookup = searchParams.get("lookup")?.trim() ?? "";
  const lookup = stateLookup || queryLookup;

  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    lookup ? "loading" : "idle",
  );
  const [result, setResult] = useState<ReferralStatus | null>(null);
  const [error, setError] = useState<string>("");

  // Support old/bookmarked lookup URLs, but remove the email/code from the
  // address bar before normal effects and future analytics captures run.
  useLayoutEffect(() => {
    if (!queryLookup) return;
    navigate("/referral", { replace: true, state: { lookup: queryLookup } });
  }, [navigate, queryLookup]);

  useEffect(() => {
    if (!lookup) {
      setStatus("idle");
      setResult(null);
      setError("");
      return;
    }
    setStatus("loading");
    setError("");
    lookupReferralStatus(lookup).then((res) => {
      if (res.ok) {
        setResult(res.status);
        setStatus("ready");
      } else {
        setError(res.message);
        setResult(null);
        setStatus("error");
      }
    });
  }, [lookup]);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.08),transparent_50%)]" />
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,hsl(var(--primary)/0.06),transparent_70%)] blur-3xl" />
      <div className="mx-auto max-w-3xl px-6 py-24 md:py-32">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-primary">
            {t("referral.eyebrow")}
          </p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            {t("referral.title")}
          </h1>
        </div>

        <div className="mt-12">
          {!lookup || status === "idle" ? (
            <LookupForm />
          ) : status === "loading" ? (
            <LoadingCard />
          ) : status === "error" ? (
            <ErrorCard message={error} />
          ) : result ? (
            <StatusCard status={result} />
          ) : null}
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          <RewardStep
            icon={Users}
            labelKey="referral.tier1Label"
            rewardNumber={1}
            rewardKey="referral.tier1Reward"
            progress={result ? { count: result.joinedCount, target: 1 } : undefined}
          />
          <RewardStep
            icon={Users}
            labelKey="referral.tier3Label"
            rewardNumber={3}
            rewardKey="referral.tier3Reward"
            extra={{
              prefixKey: "referral.tier3ExtraPrefix",
              keywordKey: "referral.tier3ExtraKeyword",
            }}
            progress={result ? { count: result.joinedCount, target: 3 } : undefined}
          />
          <RewardStep
            icon={Users}
            labelKey="referral.tier5Label"
            rewardNumber={6}
            rewardKey="referral.tier5Reward"
            extra={{
              prefixKey: "referral.tier5ExtraPrefix",
              keywordKey: "referral.tier5ExtraKeyword",
              suffixKey: "referral.tier5ExtraSuffix",
            }}
            progress={result ? { count: result.joinedCount, target: 5 } : undefined}
          />
        </div>
      </div>
    </div>
  );
}

function LookupForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [localError, setLocalError] = useState(false);
  const feedbackId = useId();

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || (!isEmail(trimmed) && !/^[a-zA-Z0-9]{10}$/.test(trimmed))) {
      setLocalError(true);
      return;
    }
    navigate("/referral", { state: { lookup: trimmed } });
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-6 shadow-sm backdrop-blur-sm md:p-8">
      <h2 className="text-lg font-semibold text-foreground">{t("referral.lookupTitle")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t("referral.lookupBody")}</p>
      <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <Input
          type="text"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setLocalError(false);
          }}
          placeholder={t("referral.lookupPlaceholder")}
          aria-label={t("referral.lookupPlaceholder")}
          aria-invalid={localError}
          aria-describedby={localError ? feedbackId : undefined}
          className="h-12 sm:flex-1"
        />
        <Button type="submit" variant="primary" size="lg" className="shrink-0">
          {t("referral.lookupSubmit")}
        </Button>
      </form>
      {localError ? (
        <p id={feedbackId} role="alert" className="mt-2 text-sm text-red-400">
          {t("referral.lookupError")}
        </p>
      ) : null}
    </div>
  );
}

function LoadingCard() {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-8 text-center shadow-sm backdrop-blur-sm">
      <p className="text-muted-foreground">{t("referral.loading")}</p>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-8 text-center shadow-sm backdrop-blur-sm">
      <p className="text-foreground">{message}</p>
      <Button variant="secondary" className="mt-4" onClick={() => navigate("/referral")}>
        {t("referral.tryAgain")}
      </Button>
    </div>
  );
}

function StatusCard({ status }: { status: ReferralStatus }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const inviteMessage = status.referralLink
    ? t("referral.inviteMessage", { link: status.referralLink })
    : "";

  // Fit the textarea to its wrapped content — line count varies with viewport
  // width (mobile wraps the message + link across more lines than desktop).
  // biome-ignore lint/correctness/useExhaustiveDependencies: value read via ref; inviteMessage triggers re-measure when the text itself changes
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const fitHeight = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    fitHeight();
    window.addEventListener("resize", fitHeight);
    return () => window.removeEventListener("resize", fitHeight);
  }, [inviteMessage]);

  async function copyLink() {
    if (!inviteMessage) return;
    try {
      await navigator.clipboard.writeText(inviteMessage);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-6 shadow-sm backdrop-blur-sm md:p-8">
      {status.referralLink ? (
        <>
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Link className="h-4 w-4" />
            </span>
            <h2 className="text-lg font-semibold text-foreground">{t("referral.yourLink")}</h2>
          </div>
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-border/60 bg-muted/30 p-4">
            <textarea
              ref={textareaRef}
              readOnly
              rows={1}
              value={inviteMessage}
              onFocus={(event) => event.target.select()}
              className="flex-1 resize-none overflow-hidden rounded-lg border-0 bg-transparent p-0 text-sm leading-snug text-foreground outline-none"
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={copyLink}
              className="shrink-0"
              aria-label={copied ? t("waitlist.linkCopied") : t("waitlist.copyLink")}
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </div>
        </>
      ) : null}

      <div className="mt-6 flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Users className="h-4 w-4" />
        </span>
        <h2 className="text-lg font-semibold text-foreground">{t("referral.progressTitle")}</h2>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{status.invitedCount}</p>
          <p className="text-xs text-muted-foreground">{t("referral.invitedLabel")}</p>
        </div>
        <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-center">
          <p className="text-2xl font-bold text-primary">{status.joinedCount}</p>
          <p className="text-xs text-muted-foreground">{t("referral.joinedLabel")}</p>
        </div>
      </div>

      {status.innerCircleEligible ? (
        <div className="mt-4 rounded-lg bg-primary/10 p-3 text-sm font-medium text-primary">
          {t("referral.innerCircleEligible")}
        </div>
      ) : null}
    </div>
  );
}

function RewardStep({
  icon: Icon,
  labelKey,
  rewardNumber,
  rewardKey,
  extra,
  progress,
}: {
  icon: React.ElementType;
  labelKey: string;
  rewardNumber: number;
  rewardKey: string;
  extra?: { prefixKey: string; keywordKey: string; suffixKey?: string };
  /** Present once the visitor has looked up a status — drives the corner badge and dimming below. */
  progress?: { count: number; target: number };
}) {
  const { t } = useTranslation();
  const unlocked = progress ? progress.count >= progress.target : false;
  return (
    <div
      className={`flex flex-col items-center rounded-2xl border p-6 text-center shadow-sm backdrop-blur-sm transition-colors ${
        progress && !unlocked
          ? "border-border/40 bg-card/30 opacity-60"
          : unlocked
            ? "border-primary/30 bg-primary/5"
            : "border-border/60 bg-card/60 hover:border-primary/20"
      }`}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="text-sm font-medium text-foreground">{t(labelKey)}</span>
        </div>
        {progress ? (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
              unlocked ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            }`}
          >
            {unlocked ? t("referral.unlocked") : `${progress.count}/${progress.target}`}
          </span>
        ) : null}
      </div>
      <div className="mt-6 flex flex-wrap items-baseline justify-center gap-1.5">
        <span className="text-sm font-medium text-foreground">{t("referral.get")}</span>
        <span className="text-2xl font-bold leading-none text-primary">{rewardNumber}</span>
        <span className="text-sm font-medium text-foreground">{t(rewardKey)}</span>
      </div>
      {extra ? (
        <>
          <div className="my-3 flex justify-center">
            <Plus className="h-5 w-5 text-muted-foreground/80" aria-hidden />
          </div>
          <p className="text-sm text-muted-foreground">
            {t(extra.prefixKey)}{" "}
            <span className="font-semibold text-primary">{t(extra.keywordKey)}</span>
            {extra.suffixKey ? ` ${t(extra.suffixKey)}` : null}
          </p>
        </>
      ) : null}
    </div>
  );
}
